import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, Check } from "lucide-react";
import { useLang } from "../context/LanguageContext";

export default function CompanyTraining() {
  const { t } = useLang();
  return (
    <div className="bg-[var(--bg)]" data-testid="company-training-page">
      {/* HERO */}
      <section className="pt-28 md:pt-32 pb-16 md:pb-20 border-b border-[color:var(--line-soft)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="overline mb-5">{t("company.hero.overline")}</p>
            <h1 className="font-serif font-light text-5xl sm:text-6xl text-[color:var(--ink)] tracking-tight leading-[1.05]">
              {t("company.hero.title.a")} <br />
              <em className="text-[color:var(--accent)] not-italic">{t("company.hero.title.b")}</em>
            </h1>
            <p className="mt-6 text-lg text-[color:var(--ink-2)] leading-relaxed max-w-xl">
              {t("company.hero.p1.a")}{" "}
              <span className="text-[color:var(--ink)] font-medium">Anthony</span>
              {", "}
              {t("company.hero.p1.b")}
            </p>
            <p className="mt-4 text-[color:var(--ink-2)] leading-relaxed max-w-xl">
              {t("company.hero.p2")}
            </p>
          </div>
          <div className="md:col-span-5">
            <img
              src="https://customer-assets.emergentagent.com/job_whole-being-coach/artifacts/usd8v22y_CD09BC6B-C0A0-4600-8FB8-C039B7C8832E.JPG"
              alt="Sopfie & Anthony — company training duo"
              className="w-full aspect-[4/5] object-cover rounded-[1.6rem] shadow-[0_30px_60px_-30px_rgba(42,39,36,0.35)]"
            />
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="overline mb-4">{t("company.why.overline")}</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-[color:var(--ink)] tracking-tight">
            {t("company.why.title")}
          </h2>
          <div className="mt-8 space-y-5 text-[color:var(--ink-2)] text-lg leading-relaxed max-w-3xl">
            <p>{t("company.why.p1")}</p>
            <p>{t("company.why.p2")}</p>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="py-20 bg-[var(--bg-2)]/60 border-y border-[color:var(--line-soft)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="overline mb-4">{t("company.offer.overline")}</p>
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-[color:var(--ink)] tracking-tight max-w-3xl">
            {t("company.offer.title")}
          </h2>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { id: "c1", title: t("company.offer.c1.title"), body: t("company.offer.c1.body") },
              { id: "c2", title: t("company.offer.c2.title"), body: t("company.offer.c2.body") },
              { id: "c3", title: t("company.offer.c3.title"), body: t("company.offer.c3.body") },
            ].map((c) => (
              <article key={c.id} className="card-soft p-7">
                <h3 className="font-serif text-2xl text-[color:var(--ink)]">{c.title}</h3>
                <p className="mt-3 text-[color:var(--ink-2)] leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* IN PERSON ADD-ON */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="card-soft p-8 md:p-10 grid md:grid-cols-12 gap-8 items-center" data-testid="company-inperson-addon">
            <div className="md:col-span-8">
              <p className="overline">{t("company.addon.overline")}</p>
              <h3 className="mt-2 font-serif font-light text-3xl text-[color:var(--ink)] tracking-tight">
                {t("company.addon.title.a")} <em className="text-[color:var(--accent)] not-italic">{t("company.addon.title.b")}</em>
              </h3>
              <p className="mt-3 text-[color:var(--ink-2)] leading-relaxed">{t("company.addon.body")}</p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <a href="mailto:Sopfie_soendergaard@hotmail.com?subject=In-person%20company%20training%20enquiry" className="btn-primary" data-testid="company-inperson-cta">
                {t("company.addon.cta")} <ArrowUpRight size={16} strokeWidth={1.6} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="py-20 bg-[var(--bg-2)]/60 border-y border-[color:var(--line-soft)]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p className="overline mb-4">{t("company.outcomes.overline")}</p>
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-[color:var(--ink)] tracking-tight">
            {t("company.outcomes.title.a")} <em className="text-[color:var(--accent)] not-italic">{t("company.outcomes.title.b")}</em>
          </h2>
          <ul className="mt-10 grid sm:grid-cols-2 gap-4 text-left max-w-3xl mx-auto">
            {[
              t("company.outcomes.li1"),
              t("company.outcomes.li2"),
              t("company.outcomes.li3"),
              t("company.outcomes.li4"),
            ].map((line) => (
              <li key={line} className="flex gap-3 items-start text-[color:var(--ink-2)]">
                <Check size={16} strokeWidth={1.6} className="text-[color:var(--accent)] mt-1 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <p className="overline mb-4">{t("company.contact.overline")}</p>
          <h2 className="font-serif font-light text-4xl text-[color:var(--ink)] tracking-tight">
            {t("company.contact.title")}
          </h2>
          <p className="mt-4 text-[color:var(--ink-2)] max-w-xl mx-auto leading-relaxed">
            {t("company.contact.lead")}
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
            <div className="card-soft p-6" data-testid="company-contact-sopfie">
              <p className="overline">Sopfie</p>
              <a href="mailto:Sopfie_soendergaard@hotmail.com?subject=Company%20training%20enquiry" className="mt-3 flex items-center gap-2 text-[color:var(--ink)] hover:text-[color:var(--accent)] break-all">
                <Mail size={16} strokeWidth={1.4} className="shrink-0" /> Sopfie_soendergaard@hotmail.com
              </a>
              <a href="tel:+4551490771" className="mt-2 flex items-center gap-2 text-[color:var(--ink)] hover:text-[color:var(--accent)]">
                <Phone size={16} strokeWidth={1.4} className="shrink-0" /> 51 49 07 71
              </a>
            </div>
            <div className="card-soft p-6" data-testid="company-contact-anthony">
              <p className="overline">Anthony</p>
              <a href="mailto:anormann2002@yahoo.com?subject=Company%20training%20enquiry" className="mt-3 flex items-center gap-2 text-[color:var(--ink)] hover:text-[color:var(--accent)] break-all" data-testid="company-anthony-email">
                <Mail size={16} strokeWidth={1.4} className="shrink-0" /> anormann2002@yahoo.com
              </a>
              <a href="tel:+4541132010" className="mt-2 flex items-center gap-2 text-[color:var(--ink)] hover:text-[color:var(--accent)]" data-testid="company-anthony-phone">
                <Phone size={16} strokeWidth={1.4} className="shrink-0" /> 41 13 20 10
              </a>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a href="mailto:Sopfie_soendergaard@hotmail.com?subject=Company%20training%20enquiry" className="btn-primary" data-testid="company-cta-email">
              {t("company.contact.cta")} <ArrowUpRight size={16} strokeWidth={1.6} />
            </a>
            <Link to="/" className="btn-ghost" data-testid="company-back-home">{t("common.back_to_home")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
